import { WindowLevelTool } from "@cornerstonejs/tools";
import { TOAST_TYPE } from "../../../constants/Constants";
import { isReconstructable } from "./MPRService";
import { loadMPR, enableVOISync } from "./CornerstoneViewportService";

/**
 * Resets the original image active state for the given viewport index.
 * Shared between Viewport (handleDrop) and Thumbnail (setActiveThumbnail).
 */
export const resetOriginalImageState = (
    originalImageActiveState,
    activeViewportIndex,
    setOriginalImageActiveState
) => {
    if (originalImageActiveState[activeViewportIndex]) {
        const temp = originalImageActiveState.map((state, index) => {
            if (index === activeViewportIndex) return false;
            return state;
        });
        setOriginalImageActiveState(temp);
    }
};

/**
 * Builds a series data object from a stack and its related stacks.
 * Used for thumbnail click, lateral series load, and initial viewport load.
 */
export const buildSeriesDataFromStack = (
    stack,
    originalStack,
    heatmapStack,
    outlineStack,
    modality,
    viewportIndex,
    thumbnailIndex
) => ({
    stack,
    studyId: stack.studyId,
    studyIdDisplay: stack.studyIdDisplay,
    series: stack.stackId,
    modality,
    seriesModality: stack.seriesModality,
    viewportIndex,
    patientId: stack.patientId,
    patientAge: stack.patientAge,
    patientSex: stack.patientSex,
    patientName: stack.patientName,
    studyDescription: stack.studyDescription,
    studyDate: stack.studyDate,
    thumbnailIndex,
    originalStack,
    heatmapStack,
    outlineStack,
    orderId: stack.orderId,
    clientName: stack.clientName,
});

/**
 * Where a study's series sits in the thumbnail list's one running sequence.
 *
 * The list numbers every thumbnail across all the open studies in a single
 * sequence, and `hPThumbnailIds` addresses them by that number — so a hang that
 * fills a viewport from a prior has to report the prior's number, not the
 * series' position within its own study.
 *
 * This mirrors what ThumbnailList and ChildStudyThumbnailList compute inline
 * while rendering, asymmetry included: the open study is numbered over its
 * *renderable* stacks (KO and PR render nothing and take no number), while a
 * prior is numbered over its whole array. Change the numbering in one place and
 * it has to change in the other.
 */
export const thumbnailIndexOf = (priorStudyData, studyIndex, seriesIndex) => {
    if (!studyIndex) return seriesIndex;

    const renderable = (stack) => stack?.modality !== "KO" && stack?.modality !== "PR";
    let offset = (priorStudyData?.[0]?.stacks ?? []).filter(renderable).length;
    for (let index = 1; index < studyIndex; index += 1) {
        offset += priorStudyData?.[index]?.stacks?.length ?? 0;
    }
    return offset + seriesIndex;
};

/**
 * Extracts series data from a drag-and-drop event into a structured object.
 * Parses the single serialized JSON blob set by handleOnDrag.
 * Used by Viewport and MPRViewport handleDrop.
 */
export const extractSeriesDataFromDragEvent = (e, viewportIndex) => {
    const seriesData = JSON.parse(e.dataTransfer.getData("application/series-data"));
    seriesData.viewportIndex = viewportIndex;
    return seriesData;
};

/**
 * Switches the series currently reconstructed in the MPR viewports — used
 * whenever MPR mode is already on and the user picks a different series,
 * either by dragging it onto an MPR pane or clicking its thumbnail. No-ops
 * if the requested series is already the one showing (`currentSeriesId`
 * matches), so a repeat drag/click doesn't rebuild the volume.
 *
 * `setMPRViewportLoaderRunning(true)` is set before the reconstructability
 * check and stays true until loadMPR settles — MPRViewport renders its
 * loader overlay opaque, so this also visually clears the panes of the
 * previous series for the whole rebuild, not just while the volume itself
 * is loading.
 */
export const switchMPRSeries = async ({
    thumbnailIndex,
    seriesData,
    currentSeriesId,
    currentVolumeId,
    setMprLoading,
    setMprViewportElements,
    setIsMPRViewportActivated,
    setVolumeId,
    setActiveMPRTool,
    setActiveMPRThumbnailIndex,
    setIsMPRActive,
    setMPRViewportLoaderRunning,
    setMprSeriesData,
    assignMouseButtonToAssignableTool,
    showToastv2,
    t,
}) => {
    // Returned to the caller so it knows whether the volume actually
    // reloaded (vs. was skipped/failed) and should run its own tail logic
    // (e.g. re-reading the initial VOI off the freshly loaded volume).
    if (currentSeriesId && currentSeriesId === seriesData.series) {
        return false;
    }

    setMPRViewportLoaderRunning(true);
    const isSeriesReconstructable = await isReconstructable(
        thumbnailIndex,
        "thumbnail"
    );

    if (!isSeriesReconstructable.value) {
        setMPRViewportLoaderRunning(false);
        showToastv2(TOAST_TYPE.ERROR, t("mpr.not-reconstructable"));
        return false;
    }
    if (isSeriesReconstructable.hasDuplicateSlice) {
        showToastv2(TOAST_TYPE.WARNING, t("mpr.duplicate-slice-warning"));
    }
    if (isSeriesReconstructable.excludedImageIds?.length) {
        showToastv2(TOAST_TYPE.WARNING, t("mpr.excluded-instances-warning"));
    }

    setActiveMPRTool(WindowLevelTool.toolName);
    assignMouseButtonToAssignableTool("primary", WindowLevelTool.toolName, true);
    setIsMPRActive(true);
    setActiveMPRThumbnailIndex(thumbnailIndex);

    try {
        await loadMPR(
            thumbnailIndex,
            "thumbnail",
            seriesData.series,
            setMprLoading,
            setMprViewportElements,
            setIsMPRViewportActivated,
            setVolumeId,
            isSeriesReconstructable.excludedImageIds,
            currentVolumeId
        );
    } catch (error) {
        console.error("[MPR] failed to load volume:", error);
        setIsMPRActive(false);
        setMPRViewportLoaderRunning(false);
        showToastv2(TOAST_TYPE.ERROR, t("mpr.load-failed"));
        return false;
    }

    setMprSeriesData({
        study: seriesData.studyId,
        series: seriesData.series,
        modality: seriesData.modality,
        patientAge: seriesData.patientAge,
        patientId: seriesData.patientId,
        studyIdDisplay: seriesData.studyIdDisplay,
        studyDate: seriesData.studyDate,
        patientName: seriesData.patientName,
        studyDescription: seriesData.studyDescription,
        orderId: seriesData.orderId,
        clientName: seriesData.clientName,
    });

    enableVOISync();
    return true;
};